
import { getQuoteOptions, calculateQuote, saveQuote, emailQuote, uploadQuoteFile, lookupCustomer } from '@/api';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import Button from '@/components/UI/button/Button.vue';
import FilesTable from '@/components/UI/filesTable/FilesTable.vue';
import UploadAttachments from '@/components/UI/UploadAttachments/UploadAttachments.vue';
import LookupCustomerCode from '@/components/jobBooking/AdvancedJobBooking/LookupCustomerCode.vue';
import CustomerCodeSelect from '@/components/dashBoard/selectsFields/CustomerCodeSelect/CustomerCodeSelect.vue';
import LookupSuburb from '@/components/UI/lookups/LookupSuburb.vue';
import * as Sentry from '@sentry/vue';
import * as routeNames from '@/router/routeNames';

import { User, SuburbLookupData } from '@/models/main';
import { AuthData } from '@/models/auth';
import { getSuburb } from '@/api';
import {
  QuoteOptions,
  JobType,
  JobTypeOption,
  QuoteItem,
  QuoteOptionItem,
  QuoteCalculateResponse,
  QuoteCalculateLine,
  QuoteCalculateRequest,
  QuoteSaveRequest,
  ActionValue,
} from '@/models/quote';

import { addDays, format } from 'date-fns';
import { Component, Vue } from 'vue-property-decorator';
import { mapState } from 'vuex';
import { notificationErrorText } from '@/utils/PortalConstants';

@Component({
  components: {
    BackButton,
    Button,
    FilesTable,
    UploadAttachments,
    LookupCustomerCode,
    CustomerCodeSelect,
    LookupSuburb,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
    ...mapState('moduleAuthData', ['authData']),
  },
})
export default class QuoteCalculator extends Vue {
  public routeNames = routeNames;
  public autocompleteCustomer = lookupCustomer;
  private user: User;
  private authData: AuthData;

  // Options from API
  private options: QuoteOptions | null = null;
  private optionsLoaded = false;
  // Token guarding async option loads against rapid customer switches.
  private optionsLoadToken = 0;

  // Customer code
  private customerCode = '';

  // Job-level fields
  private jobType: JobType | '' = '';
  private surcharges: string[] = [];

  // Items
  private items: QuoteItem[] = [this.createEmptyItem()];

  // Area
  private areaType: 'metro' | 'regional' = 'metro';
  private fromSuburb = '';
  private fromPostcode = '';
  private toSuburb = '';
  private toPostcode = '';
  public autocompleteSuburb = getSuburb;
  private fromSuburbChosen = {
    suburb: '',
    suburbId: -1,
    postcode: '',
    state: '',
  };
  private toSuburbChosen = {
    suburb: '',
    suburbId: -1,
    postcode: '',
    state: '',
  };

  // Calculation results
  private calculating = false;
  private calculationResult: QuoteCalculateResponse | null = null;

  // File attachments (held in memory until save)
  private files: File[] = [];

  // Save state
  private reference = '';
  private notes = '';
  private validUntil = '';
  private dateMenu = false;
  private saving = false;
  private savingAndEmailing = false;
  private showEmailDialog = false;
  private emailTo = '';
  private dialogError = '';

  // Computed: dropdown options from API
  get machineOptions(): QuoteOptionItem[] {
    return this.options?.machine_sizes || [];
  }

  get jobTypeOptions(): JobTypeOption[] {
    return this.options?.job_types || [];
  }

  get surchargeOptions(): QuoteOptionItem[] {
    return this.options?.surcharges || [];
  }

  get techLevelOptions(): QuoteOptionItem[] {
    return this.options?.tech_levels || [];
  }

  get actionOptions(): QuoteOptionItem[] {
    return this.options?.actions || [];
  }

  get currentJobTypeSpec(): JobTypeOption | null {
    return this.jobTypeOptions.find(jt => jt.value === this.jobType) || null;
  }

  get showPerItemAction(): boolean {
    return !!this.currentJobTypeSpec?.per_item_action;
  }

  // Filter the global action list to those the current job type permits.
  get actionOptionsForCurrentJob(): QuoteOptionItem[] {
    const allowed = this.currentJobTypeSpec?.available_actions ?? [];
    if (allowed.length === 0) {
      return this.actionOptions;
    }
    return this.actionOptions.filter(a => allowed.includes(a.value as ActionValue));
  }

  get jobTypeHint(): string {
    switch (this.jobType) {
      case 'delivery':
        return 'Warehouse → client. Pick an action per item.';
      case 'pickup':
        return 'Client → warehouse.';
      case 'relocate_internal':
        return 'Same client site (e.g. floor-to-floor).';
      case 'relocate_site':
        return 'Different client sites. Pick both From and To suburbs in Regional mode.';
      case 'deliver_legacy_collection':
        return 'Round-trip: deliver new device, collect legacy device on the return leg.';
      default:
        return '';
    }
  }

  get todayDate() {
    return format(new Date(), 'yyyy-MM-dd');
  }

  // Service lines only (excluding fuel levy)
  get serviceLines(): QuoteCalculateLine[] {
    return this.calculationResult?.lines.filter(l => !l.is_fuel_levy) ?? [];
  }

  get hasFuelLevy(): boolean {
    if (!this.calculationResult) {
      return false;
    }
    return parseFloat(this.calculationResult.fuel_levy_ex_gst || '0') > 0;
  }

  get subtotalExGst(): string {
    return this.sumServiceLines('fee_ex_gst');
  }

  get subtotalGst(): string {
    return this.sumServiceLines('gst');
  }

  get subtotalIncGst(): string {
    return this.sumServiceLines('fee_inc_gst');
  }

  private sumServiceLines(field: 'fee_ex_gst' | 'gst' | 'fee_inc_gst'): string {
    let total = 0;
    for (const line of this.serviceLines) {
      total += parseFloat(line[field] || '0');
    }
    return total.toFixed(2);
  }

  // Lifecycle
  async mounted() {
    this.validUntil = this.getDefaultValidUntil();

    if (this.$ability.can('staff', 'Job')) {
      this.optionsLoaded = false;
    } else {
      const codes = this.authData?.availableCustomerCodes || [];
      if (codes.length > 0) {
        this.customerCode = codes[0];
        await this.loadOptions(this.customerCode);
      }
    }
  }

  // Methods
  private createEmptyItem(): QuoteItem {
    return {
      machine_size: '',
      quantity: 1,
      weight: 0,
      action: null,
      tech_level: null,
    };
  }

  private addItem() {
    this.items.push(this.createEmptyItem());
  }

  private removeItem(index: number) {
    if (this.items.length > 1) {
      this.items.splice(index, 1);
    }
  }

  private async loadOptions(customerCode?: string) {
    const token = ++this.optionsLoadToken;
    try {
      const result = await getQuoteOptions(customerCode);
      if (token !== this.optionsLoadToken) {
        return;
      }
      this.options = result;
      this.optionsLoaded = true;
    } catch (err: any) {
      if (token !== this.optionsLoadToken) {
        return;
      }
      this.notifyError(err, 'Failed to load options for this customer.');
      this.optionsLoaded = false;
    }
  }

  private async onCustomerCodeChange(code: string) {
    this.customerCode = code;
    this.calculationResult = null;
    // Reset selections that depend on the previous customer's option list
    // so stale codes (invisible in the UI) aren't carried into the next request.
    this.surcharges = [];
    await this.loadOptions(code);
  }

  private onCustomerCodeClear() {
    // Invalidate any in-flight load so a stale response can't repopulate options.
    ++this.optionsLoadToken;
    this.customerCode = '';
    this.options = null;
    this.optionsLoaded = false;
    this.calculationResult = null;
    this.surcharges = [];
  }

  private onJobTypeChange() {
    this.calculationResult = null;
    if (!this.showPerItemAction) {
      for (const it of this.items) {
        it.action = null;
        it.tech_level = null;
      }
    } else {
      // If current per-item action is no longer allowed under the new job type, clear it.
      const allowed = this.currentJobTypeSpec?.available_actions ?? [];
      for (const it of this.items) {
        if (it.action && allowed.length > 0 && !allowed.includes(it.action)) {
          it.action = null;
          it.tech_level = null;
        }
      }
    }
  }

  private onItemActionChange(item: QuoteItem) {
    if (item.action !== 'deliver_test') {
      item.tech_level = null;
    }
  }

  private onFromSuburbChange(stop: number, item: SuburbLookupData) {
    this.fromSuburb = item.name;
    this.fromPostcode = item.postcode;
  }

  private onFromSuburbClear() {
    this.fromSuburb = '';
    this.fromPostcode = '';
  }

  private onToSuburbChange(stop: number, item: SuburbLookupData) {
    this.toSuburb = item.name;
    this.toPostcode = item.postcode;
  }

  private onToSuburbClear() {
    this.toSuburb = '';
    this.toPostcode = '';
  }

  private getFromSuburb(): string {
    return this.areaType === 'metro' ? this.options?.default_suburb || '' : this.fromSuburb;
  }

  private getFromPostcode(): string {
    return this.areaType === 'metro' ? this.options?.default_postcode || '' : this.fromPostcode;
  }

  private getToSuburb(): string {
    if (this.areaType === 'regional') {
      return this.toSuburb || this.fromSuburb;
    }
    return this.getFromSuburb();
  }

  private getToPostcode(): string {
    if (this.areaType === 'regional') {
      return this.toPostcode || this.fromPostcode;
    }
    return this.getFromPostcode();
  }

  private buildRequest(): QuoteCalculateRequest {
    const req: QuoteCalculateRequest = {
      job_type: this.jobType as JobType,
      surcharges: this.surcharges,
      items: this.items.map(it => {
        const out: QuoteItem = {
          machine_size: it.machine_size,
          quantity: it.quantity,
          weight: it.weight ?? 0,
        };
        if (this.showPerItemAction && it.action) {
          out.action = it.action;
          if (it.action === 'deliver_test' && it.tech_level) {
            out.tech_level = it.tech_level;
          }
        }
        return out;
      }),
    };
    if (this.customerCode) {
      req.customer_code = this.customerCode;
    }
    const fromSub = this.getFromSuburb();
    const toSub = this.getToSuburb();
    if (fromSub) {
      req.from_suburb = fromSub;
      req.from_postcode = this.getFromPostcode();
    }
    if (toSub) {
      req.to_suburb = toSub;
      req.to_postcode = this.getToPostcode();
    }
    return req;
  }

  private async calculate() {
    const form = this.$refs.form as any;
    if (!form.validate()) {
      return;
    }

    if (!this.customerCode) {
      this.$notify({
        group: 'network',
        text: 'Please select a customer code first.',
        type: 'warn',
        duration: 4000,
      });
      return;
    }

    if (this.areaType === 'regional') {
      if (!this.fromSuburb || !this.toSuburb) {
        this.$notify({
          group: 'network',
          text: 'Please select both From and To suburbs for regional delivery.',
          type: 'warn',
          duration: 4000,
        });
        return;
      }
      if (
        this.jobType === 'relocate_site' &&
        this.fromSuburb.toUpperCase() === this.toSuburb.toUpperCase() &&
        this.fromPostcode === this.toPostcode
      ) {
        this.$notify({
          group: 'network',
          text: 'From and To suburbs must differ for Relocate (between sites).',
          type: 'warn',
          duration: 4000,
        });
        return;
      }
    }

    this.calculating = true;
    this.calculationResult = null;

    try {
      this.calculationResult = await calculateQuote(this.buildRequest());
      setTimeout(() => {
        const el = this.$refs.resultsSection as HTMLElement;
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 350);
    } catch (err: any) {
      this.notifyError(err, 'Calculation failed. Please check your inputs.');
    } finally {
      this.calculating = false;
    }
  }

  private buildSavePayload(): QuoteSaveRequest {
    return {
      ...this.buildRequest(),
      reference: this.reference,
      notes: this.notes,
      ...(this.validUntil ? { valid_until: this.validUntil } : {}),
    };
  }

  // Save only
  private async saveQuoteAction() {
    if (!this.calculationResult) {
      return;
    }
    this.saving = true;
    try {
      const savedQuote = await saveQuote(this.buildSavePayload());
      if (this.files.length > 0) {
        await this.uploadFiles(savedQuote.id);
      }
      this.$notify({
        group: 'network',
        text: 'Quote saved!',
        type: 'success',
        duration: 3000,
      });
      this.$router.push({
        name: routeNames.DETAILED_QUOTE,
        params: { id: String(savedQuote.id) },
      });
    } catch (err: any) {
      this.notifyError(err, 'Failed to save quote.');
    } finally {
      this.saving = false;
    }
  }

  // Save then email (called from dialog)
  private async saveAndEmail() {
    if (!this.calculationResult || !this.emailTo) {
      return;
    }
    this.savingAndEmailing = true;
    this.dialogError = '';
    try {
      const savedQuote = await saveQuote(this.buildSavePayload());
      if (this.files.length > 0) {
        await this.uploadFiles(savedQuote.id);
      }
      await emailQuote(savedQuote.id, this.emailTo);
      this.showEmailDialog = false;
      this.$notify({
        group: 'network',
        text: `Quote saved and emailed to ${this.emailTo}`,
        type: 'success',
        duration: 3000,
      });
      this.$router.push({
        name: routeNames.DETAILED_QUOTE,
        params: { id: String(savedQuote.id) },
      });
    } catch (err: any) {
      this.dialogError = this.extractErrorMessage(err, 'Failed to save and email quote.');
    } finally {
      this.savingAndEmailing = false;
    }
  }

  private changeFiles(files: File[]): void {
    this.files = [...this.files, ...files];
  }

  private deleteFile(fileIndex: number): void {
    this.files.splice(fileIndex, 1);
  }

  private async uploadFiles(quoteId: number): Promise<void> {
    for (const file of this.files) {
      try {
        await uploadQuoteFile(quoteId, file);
      } catch {
        this.$notify({
          group: 'network',
          text: 'Some attachments failed to upload.',
          type: 'warn',
          duration: 5000,
        });
        return;
      }
    }
  }

  private closeEmailDialog() {
    this.showEmailDialog = false;
    this.dialogError = '';
  }

  private extractErrorMessage(err: any, fallback: string): string {
    if (err?.response?.status === 400) {
      const data = err.response.data;
      if (data?.error) {
        return data.error;
      }
      if (data?.email_to) {
        return data.email_to.join(', ');
      }
      return fallback;
    }
    Sentry.captureException(err);
    return notificationErrorText;
  }

  private notifyError(err: any, fallback: string) {
    let msg = fallback;
    if (err?.response?.status === 400) {
      msg = err.response.data?.error || fallback;
    } else {
      Sentry.captureException(err);
      msg = notificationErrorText;
    }
    this.$notify({ group: 'network', text: msg, type: 'warn', duration: 5000 });
  }

  private getDefaultValidUntil(): string {
    let date = new Date();
    let businessDays = 0;
    while (businessDays < 7) {
      date = addDays(date, 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) {
        businessDays++;
      }
    }
    return format(date, 'yyyy-MM-dd');
  }
}
